import { TableFilterCheckboxHeaderCell } from '@/components/table-selection/TableSelectionHeaderControls';
import { SortDirection } from '@/enums/sort';
import Input from '@/ui/Input';
import Rating from '@/ui/Rating';
import { TableColumn } from '@/ui/Table';
import clsx from 'clsx';
import { ReactNode } from 'react';

const GearIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.3 3.4h3.4l.6 2a6.6 6.6 0 0 1 1.4.8l2-.8 1.7 2.9-1.5 1.5c.2.5.3 1 .4 1.5l2 .6v3.4l-2 .6a7 7 0 0 1-.4 1.5l1.5 1.5-1.7 2.9-2-.8a6.6 6.6 0 0 1-1.4.8l-.6 2h-3.4l-.6-2a6.6 6.6 0 0 1-1.4-.8l-2 .8-1.7-2.9L6 17.5a7 7 0 0 1-.4-1.5l-2-.6v-3.4l2-.6a7 7 0 0 1 .4-1.5L4.5 8.4l1.7-2.9 2 .8a6.6 6.6 0 0 1 1.4-.8l.7-2Z"
        />
        <circle cx="12" cy="12" r="2.4" />
    </svg>
);

export const formatTableMetricNumber = (value: number): string =>
    value.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

export const renderGroupTableRatingCell = (value: unknown): ReactNode => {
    if (typeof value !== 'number') {
        return '-';
    }

    return (
        <div className="flex items-center gap-2">
            <span>{formatTableMetricNumber(value)}</span>
            <Rating value={value} />
        </div>
    );
};

export const renderGroupTablePercentCell = (value: unknown): ReactNode => {
    if (typeof value !== 'number') {
        return '-';
    }

    return `${formatTableMetricNumber(value)}%`;
};

export const renderGroupTableTextFilterInput = ({
    id,
    ariaLabel,
    placeholder = 'Фильтр',
    value,
    onChange,
}: {
    id: string;
    ariaLabel: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
}): ReactNode => (
    <Input
        id={id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange((event.target as HTMLInputElement).value)}
        uiSize="compact"
        className="h-[34px] min-h-[34px] min-w-0 w-full bg-white"
    />
);

export const renderGroupTableCountDetailsCell = ({
    count,
    details,
}: {
    count: number;
    details: string[];
}): ReactNode => {
    const detailsText = details.join(', ');
    const title = detailsText ? `${count}: ${detailsText}` : String(count);

    return (
        <div title={title} className="w-full min-w-0 line-clamp-2 leading-tight break-words whitespace-normal">
            <span>{count}</span>
            {!!detailsText && <span className="text-xs text-black/50">{` ${detailsText}`}</span>}
        </div>
    );
};

export function renderGroupTableManageButton<TRecord extends { name: string }>(
    record: TRecord,
    onOpen: (record: TRecord) => void,
): ReactNode {
    return (
        <button
            type="button"
            aria-label={`Редактировать группу ${record.name}`}
            className="flex h-7 min-w-7 items-center justify-center rounded border border-slate-300 bg-white px-1 text-slate-700 transition-colors hover:bg-slate-100"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpen(record);
            }}
        >
            <GearIcon />
        </button>
    );
}

type GroupTableCommonColumnsRecord = {
    name: string;
    averageRating: number | null;
    nps: number | null;
    reviewCount: number;
    answerCount: number;
    answerPercent: number | null;
    aiAnalysisCount: number;
};

const DEFAULT_GROUP_TABLE_NAME_COLUMN_CLASS_NAME =
    'min-w-[180px] md:min-w-[240px] max-w-[58vw] md:max-w-[38vw] lg:max-w-none';

export const createGroupTableNameColumn = <TRecord extends GroupTableCommonColumnsRecord>({
    alias = 'Группа',
    className = DEFAULT_GROUP_TABLE_NAME_COLUMN_CLASS_NAME,
    render,
}: {
    alias?: string;
    className?: string;
    render?: TableColumn<TRecord>['render'];
} = {}): TableColumn<TRecord> => ({
    key: 'name',
    alias,
    sort: SortDirection.NONE,
    className,
    render,
});

export const createGroupTableAverageRatingColumn = <
    TRecord extends GroupTableCommonColumnsRecord,
>(): TableColumn<TRecord> => ({
    key: 'averageRating',
    alias: 'Оценка',
    sort: SortDirection.NONE,
    className: 'min-w-[124px] md:min-w-[148px] lg:whitespace-nowrap',
    render: (value) => renderGroupTableRatingCell(value),
});

export const createGroupTableReviewCountColumn = <
    TRecord extends GroupTableCommonColumnsRecord,
>(): TableColumn<TRecord> => ({
    key: 'reviewCount',
    alias: 'Отзывов',
    sort: SortDirection.NONE,
    className: 'min-w-[60px] md:min-w-[72px] lg:whitespace-nowrap',
});

export const createGroupTableNpsColumn = <TRecord extends GroupTableCommonColumnsRecord>(): TableColumn<TRecord> => ({
    key: 'nps',
    alias: 'NPS',
    sort: SortDirection.NONE,
    className: 'min-w-[76px] md:min-w-[88px] lg:whitespace-nowrap',
    render: (value) => renderGroupTablePercentCell(value),
});

export const createGroupTableAnswerCountColumn = <
    TRecord extends GroupTableCommonColumnsRecord,
>(): TableColumn<TRecord> => ({
    key: 'answerCount',
    alias: 'Ответов',
    sort: SortDirection.NONE,
    className: 'min-w-[60px] md:min-w-[72px] lg:whitespace-nowrap',
});

export const createGroupTableAnswerPercentColumn = <
    TRecord extends GroupTableCommonColumnsRecord,
>(): TableColumn<TRecord> => ({
    key: 'answerPercent',
    alias: '%',
    sort: SortDirection.NONE,
    className: 'min-w-[60px] md:min-w-[72px] lg:whitespace-nowrap',
    render: (value) => renderGroupTablePercentCell(value),
});

export const createGroupTableAiAnalysisCountColumn = <
    TRecord extends GroupTableCommonColumnsRecord,
>(): TableColumn<TRecord> => ({
    key: 'aiAnalysisCount',
    alias: 'Изучено',
    sort: SortDirection.NONE,
    className: 'min-w-[60px] md:min-w-[72px] lg:whitespace-nowrap',
});

export const createGroupTableMetricColumns = <TRecord extends GroupTableCommonColumnsRecord>(): Array<
    TableColumn<TRecord>
> => [
    createGroupTableAverageRatingColumn<TRecord>(),
    createGroupTableNpsColumn<TRecord>(),
    createGroupTableReviewCountColumn<TRecord>(),
    createGroupTableAnswerCountColumn<TRecord>(),
    createGroupTableAnswerPercentColumn<TRecord>(),
    createGroupTableAiAnalysisCountColumn<TRecord>(),
];

export const createGroupTableManageColumn = <TRecord extends { name: string; manage: boolean }>({
    onOpen,
}: {
    onOpen: (record: TRecord) => void;
}): TableColumn<TRecord> => ({
    key: 'manage',
    alias: '⚙',
    className: 'w-[1%] min-w-[40px] lg:whitespace-nowrap',
    render: (_, record) => renderGroupTableManageButton(record as TRecord, onOpen),
});

export function createGroupTableHeaderSecondaryRow<TData>({
    columns,
    hasSelectionColumn,
    isHeaderChecked,
    onHeaderCheckboxChange,
    renderFilterCell,
}: {
    columns: TableColumn<TData>[];
    hasSelectionColumn: boolean;
    isHeaderChecked: boolean;
    onHeaderCheckboxChange: (checked: boolean) => void;
    renderFilterCell: (column: TableColumn<TData>) => ReactNode;
}) {
    return (
        <tr className="h-10 border-b border-slate-200 bg-gray-50">
            {hasSelectionColumn && (
                <TableFilterCheckboxHeaderCell
                    key="header-filter-checkbox"
                    isChecked={isHeaderChecked}
                    onChange={onHeaderCheckboxChange}
                />
            )}
            {columns.map((column) => (
                <th
                    key={`${column.alias}-filter`}
                    className={clsx('h-10 px-1 py-0 text-start align-middle', column.className)}
                >
                    {renderFilterCell(column)}
                </th>
            ))}
        </tr>
    );
}

export function createGroupTableHeaderSecondaryRowWithNameFilter<TData>({
    columns,
    hasSelectionColumn,
    isHeaderChecked,
    onHeaderCheckboxChange,
    nameFilterInput,
    renderAdditionalFilterCell,
}: {
    columns: TableColumn<TData>[];
    hasSelectionColumn: boolean;
    isHeaderChecked: boolean;
    onHeaderCheckboxChange: (checked: boolean) => void;
    nameFilterInput: {
        id: string;
        ariaLabel: string;
        placeholder?: string;
        value: string;
        onChange: (value: string) => void;
    };
    renderAdditionalFilterCell?: (column: TableColumn<TData>) => ReactNode;
}) {
    return createGroupTableHeaderSecondaryRow({
        columns,
        hasSelectionColumn,
        isHeaderChecked,
        onHeaderCheckboxChange,
        renderFilterCell: (column) => {
            if (column.key === 'name') {
                return renderGroupTableTextFilterInput(nameFilterInput);
            }

            return renderAdditionalFilterCell ? renderAdditionalFilterCell(column) : null;
        },
    });
}
